import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import 'date-fns';
import Grid from '@mui/material/Grid';
import {
	InputLabel,
	FormControl,
	InputAdornment,
	OutlinedInput,
	Radio,
	FormLabel,
	RadioGroup,
	FormControlLabel,
	Typography,
} from '@mui/material';
import Icon from '@mui/material/Icon';
import Button from '@mui/material/Button';
import BaseLayout from 'views/BaseLayout';
import PaymentTable from './PaymentTable';
import { CampaignPayment } from '@horasys/bellplus/common/types';
import { useAppDispatch, useAppSelector } from 'Stores/Hooks/hooks';
import { CampaignState, UPDATE_CAMPAIGN_FORM } from 'Stores/Slices/campaignSlice';
import { RootState } from 'Stores/store';
import { CssTextField } from 'views/Common/CssTextField';
const PREFIX = 'PaymentDetails';

const classes = {
	formElement: `${PREFIX}-formElement`,
	absolute: `${PREFIX}-absolute`,
	fabIcon: `${PREFIX}-fabIcon`,
	paperchildGrid: `${PREFIX}-paperchildGrid`,
	saveGrid: `${PREFIX}-saveGrid`,
	currency: `${PREFIX}-currency`,
	bankDetailsGrid: `${PREFIX}-bankDetailsGrid`,
};
const Root = styled('div')(({ theme }) => ({
	[`& .${classes.formElement}`]: {
		minWidth: '100%',
		marginTop: theme.spacing(1),
	},

	[`& .${classes.absolute}`]: {},

	[`& .${classes.fabIcon}`]: {
		color: theme.palette.common.white,
		backgroundColor: theme.palette.primary.main,
		'&:hover': {
			backgroundColor: theme.palette.primary.light,
		},
	},

	[`& .${classes.paperchildGrid}`]: {
		padding: theme.spacing(1),
	},

	[`& .${classes.saveGrid}`]: {
		marginTop: theme.spacing(3),
		paddingBottom: theme.spacing(2),
	},

	[`& .${classes.currency}`]: {
		fontWeight: 900,
	},

	[`& .${classes.bankDetailsGrid}`]: {
		backgroundColor: '#1CE5E5',
		borderColor: '#ff0000',
		padding: theme.spacing(1),
	},
}));

const PaymentDetails = ({
	handleNext,
	handleBack,
}: {
	handleNext: any;
	handleBack: any;
}) => {
	const dispatch = useAppDispatch();
	const [viewMode, setViewMode] = useState<boolean>(false);
	const [disable, setDisable] = useState<boolean>(true);
	const [paymentFormData, setPaymentFormData] = useState<CampaignPayment>({
		index: -1,
		amount: 0,
		totalAmount: 0,
		description: '',
		method: '',
		option: '',
		dueDate: null,
		dueAmount: 0,
		amountPaid: 0,
		id: 0,
		status: '',
	});

	const campaignStore: CampaignState = useAppSelector(
		(state: RootState) => state.campaign,
	);
	const campaign = campaignStore.campaign;

	useEffect(() => {
		if (campaign.payments.length === 0) {
			const paymentObj: CampaignPayment = {
				index: -1,
				totalAmount: campaign.price,
				amount:
					campaign.price -
					campaign.payments.reduce((a: any, b: any) => a + b.amount, 0),
				description: '',
				method: 'Credit Card',
				option: 'Full Payment',
				dueDate: null,
				dueAmount: 0,
				discount: 0,
				amountPaid: campaign.payments.reduce((a: any, b: any) => a + b.amount, 0),
				id: 0,
				status: '',
			};
			setPaymentFormData(paymentObj);
		} else {
			const paymentObj: CampaignPayment = {
				index: -1,
				totalAmount: campaign.payments[0].totalAmount,
				amount:
					campaign.payments[0].totalAmount -
					((campaign.payments[0]?.discount
						? campaign.payments[0].discount
						: 0) /
						100) *
						campaign.payments[0].totalAmount, //
				description: '',
				method: 'Credit Card',
				option: 'Full Payment',
				dueDate: null,
				dueAmount: 0,
				discount: campaign.payments[0]?.discount
					? campaign.payments[0].discount
					: 0,
				amountPaid: 0,
				id: 0,
				status: '',
			};
			setPaymentFormData(paymentObj);
		}
	}, [campaign.payments, campaign.price]);
	const handleSubmit = () => {
		if (campaign.payments.length > 0) {
			handleNext();
		}
	};

	const handleDelete = (selectedIndex: any) => {
		const newPaymentData = campaign.payments.filter((payment, index) => {
			if (index !== selectedIndex) {
				return payment;
			} else return false;
		});
		dispatch(UPDATE_CAMPAIGN_FORM({ payments: newPaymentData }));
		resetForm();
		setViewMode(false);
	};

	const handleEdit = (selectedIndex: any) => {
		const newPaymentData = campaign.payments.filter((payment, index) => {
			if (index === selectedIndex) {
				return payment;
			} else return false;
		});
		setPaymentFormData(newPaymentData[0]);
		setViewMode(false);
	};

	const handleView = (selectedIndex: any) => {
		const newPaymentData = campaign.payments.filter((payment, index) => {
			if (index === selectedIndex) {
				return payment;
			} else return false;
		});
		setPaymentFormData(newPaymentData[0]);
		setViewMode(true);
	};

	const handleSavePayment = (): void => {
		if (paymentFormData.index === -1) {
			const paymentObj: CampaignPayment = {
				...paymentFormData,
				index: campaign.payments.length,
			};
			dispatch(
				UPDATE_CAMPAIGN_FORM({
					payments: [paymentObj],
					price:
						paymentFormData?.totalAmount -
						((paymentObj.discount ? paymentObj.discount : 0) / 100) *
							paymentFormData?.totalAmount,
				}),
			);
			resetForm();
		} else {
			const newPayments = campaign.payments.map((payment: any) => {
				if (payment.index === paymentFormData.index) {
					return paymentFormData;
				} else {
					return payment;
				}
			});
			dispatch(UPDATE_CAMPAIGN_FORM({ payments: newPayments }));
			resetForm();
		}
	};
	const resetForm = () => {
		const tempObj: CampaignPayment = {
			index: -1,
			amount: 0,
			totalAmount: 0,
			description: '',
			method: '',
			option: '',
			dueDate: null,
			dueAmount: 0,
			id: 0,
			amountPaid: 0,
			status: '',
		};
		setPaymentFormData(tempObj);
	};

	const handlePaymentForm = (event: any) => {
		const tempPaymentDetails: CampaignPayment | any = { ...paymentFormData };
		const tempval = tempPaymentDetails.totalAmount - tempPaymentDetails.amountPaid;
		switch (event.target.name) {
			case 'amount':
				tempPaymentDetails.amount = event.target.value;
				tempPaymentDetails.dueAmount = tempval - tempPaymentDetails.amount;
				break;

			case 'description':
				tempPaymentDetails.description = event.target.value;
				break;

			case 'dueAmount':
				tempPaymentDetails.dueAmount = event.target.value;
				break;

			case 'dueDate':
				tempPaymentDetails.dueDate = event.target.value;
				break;

			case 'method':
				tempPaymentDetails.method = event.target.value;
				break;

			case 'discount':
				tempPaymentDetails.discount = event.target.value;
				if (event.target.value < 0) tempPaymentDetails.discount = 0;
				if (event.target.value > 100) tempPaymentDetails.discount = 100;
				tempPaymentDetails.amount =
					tempPaymentDetails.totalAmount -
					(tempPaymentDetails.discount / 100) * tempPaymentDetails.totalAmount;
				break;

			case 'option':
				tempPaymentDetails.option = event.target.value;
				if (event.target.value === 'Pay Later') {
					tempPaymentDetails.amount = 0;
					const tempval =
						tempPaymentDetails.totalAmount - tempPaymentDetails.amountPaid;
					tempPaymentDetails.dueAmount = tempval;
					setDisable(true);
				}
				if (event.target.value === 'Complimentary Free') {
					tempPaymentDetails.amount = 0;
					tempPaymentDetails.dueAmount = 0;
					setDisable(true);
				}
				break;
		}

		setPaymentFormData(tempPaymentDetails);
	};

	return (
		<Root>
			<Grid item container justifyContent="flex-end" xs={12}></Grid>
			<BaseLayout xs={12}>
				<form noValidate>
					<Grid item container xs={12} direction="row">
						<Grid item xs={2}></Grid>
						<Grid
							item
							container
							justifyContent="space-between"
							xs={8}
							className={classes.paperchildGrid}>
							<Grid item xs={4}>
								<img
									height="100%"
									src="http://159.65.152.244/asset/qrcode/QRCode-Payment.png"
									alt="bellqrcode"
								/>
							</Grid>
							<Grid item xs={4} className={classes.bankDetailsGrid}>
								<Grid>
									<Typography
										align="center"
										component="h4"
										variant="h4">
										Bellplus Bank details:
									</Typography>
									<Typography>Name: BELLPLUS MEDIA</Typography>
									<Typography>
										Account number: 50200045402176
									</Typography>
									<Typography>Bank Name: HDFC Bank</Typography>
									<Typography>Branch: Kavuri Hills</Typography>
									<Typography>IFSC CODE: HDFC0009587</Typography>
								</Grid>
							</Grid>
						</Grid>
						<Grid container direction="row" xs={12}>
							<Grid item xs={2}></Grid>
							<Grid
								item
								container
								xs={8}
								className={classes.paperchildGrid}>
								{/* <FormControl
                                    variant="outlined"
                                    className={classes.formElement}
                                    disabled={viewMode}> */}
								{/* <FormLabel component="legend">
                                        Payment Options
                                    </FormLabel> */}
								{/* <RadioGroup
                                        row
                                        aria-label="paymentOption"
                                        name="option"
                                        value={paymentFormData.option}
                                        onChange={handlePaymentForm}>
                                        <FormControlLabel
                                            value="Full Payment"
                                            control={<Radio />}
                                            label="Full Payment"
                                        />

                                        {/* <FormControlLabel
                                            value="Complimentary Free"
                                            control={<Radio />}
                                            label="Complimentary - Free"
                                        /> */}
								{/* </RadioGroup> */}
								{/* </FormControl> */}
								<FormControl
									variant="outlined"
									className={classes.formElement}
									disabled={viewMode}>
									<FormLabel component="legend">
										Payment Method bdkjbsdkjcbdsk
									</FormLabel>
									<RadioGroup
										row
										aria-label="paymentMethod"
										name="method"
										value={paymentFormData.method}
										onChange={handlePaymentForm}>
										<FormControlLabel
											value="Credit Card"
											control={<Radio style={{color:"#042630"}}/>}
											label="Credit Card"
										/>
										<FormControlLabel
											value="Debit Card"
											control={<Radio style={{color:"#042630"}}/>}
											label="Debit Card"
										/>
										<FormControlLabel
											value="UPI"
											control={<Radio style={{color:"#042630"}}/>}
											label="UPI"
										/>
										<FormControlLabel
											value="Cheque Payment"
											control={<Radio style={{color:"#042630"}}/>}
											label="Cheque Payment"
										/>
										<FormControlLabel
											value="Paytm"
											control={<Radio style={{color:"#042630"}}/>}
											label="Paytm"
										/>
										<FormControlLabel
											value="Cash"
											control={<Radio style={{color:"#042630"}}/>}
											label="Cash"
										/>
									</RadioGroup>
								</FormControl>
								<Grid item xs={2}></Grid>
							</Grid>
						</Grid>

						<Grid item container xs={12} direction="row">
							<Grid item xs={2}></Grid>
							<Grid
								item
								container
								xs={8}
								className={classes.paperchildGrid}>
								<FormControl
									// fullWidth
									className={classes.formElement}
									variant="outlined">
									<InputLabel htmlFor="paymentAmount">
										Total Amount
									</InputLabel>
									<OutlinedInput
										style={{ width: '100%' }}
										id="paymentAmount"
										name="amount"
										value={paymentFormData.totalAmount}
										// onChange={handlePaymentForm}
										startAdornment={
											<InputAdornment position="start">
												₹
											</InputAdornment>
										}
										// labelWidth={60}
										sx={{ width: 60 }}
										disabled={true}
										type="number"
										label="Total Amount"
									/>
								</FormControl>
							</Grid>
							<Grid item xs={2}></Grid>
						</Grid>
						<Grid item container xs={12} direction="row">
							<Grid item xs={2}></Grid>
							<Grid
								item
								container
								xs={8}
								className={classes.paperchildGrid}>
								<FormControl
									fullWidth
									className={classes.formElement}
									variant="outlined">
									<InputLabel htmlFor="paymentAmount">
										Total Amount Paid
									</InputLabel>
									<OutlinedInput
										style={{ width: '100%' }}
										id="paymentAmount"
										name="amount"
										value={paymentFormData.amountPaid}
										// onChange={handlePaymentForm}
										startAdornment={
											<InputAdornment position="start">
												₹
											</InputAdornment>
										}
										// labelWidth={60}
										sx={{ width: 60 }}
										disabled={true}
										type="number"
										label="Total Amount Paid"
									/>
								</FormControl>
							</Grid>
							<Grid item xs={2}></Grid>
						</Grid>
						{/* <Grid item container xs={12} direction="row">
                            <Grid item xs={2}></Grid>
                            <Grid
                                item
                                container
                                xs={8}
                                className={classes.paperchildGrid}>
                                <FormControl
                                    fullWidth
                                    className={classes.formElement}
                                    variant="outlined">
                                    <InputLabel htmlFor="paymentAmount">
                                        Discount Percentage
                                    </InputLabel>
                                    <OutlinedInput
                                        id="paymentAmount"
                                        name="discount"
                                        placeholder="discount in %"
                                        value={paymentFormData.discount}
                                        onChange={handlePaymentForm}
                                        startAdornment={
                                            <InputAdornment position="start"></InputAdornment>
                                        }
                                        labelWidth={60}
                                        disabled={true}
                                        type="number"
                                        label="Discount Percentage"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={2}></Grid>
                        </Grid> */}
						<Grid item container xs={12} direction="row">
							<Grid item xs={2}></Grid>
							<Grid
								item
								container
								xs={8}
								className={classes.paperchildGrid}>
								<FormControl
									fullWidth
									className={classes.formElement}
									variant="outlined">
									<InputLabel htmlFor="paymentAmount">
										Amount
									</InputLabel>
									<OutlinedInput
										style={{ width: '100%' }}
										id="paymentAmount"
										name="amount"
										value={paymentFormData.amount}
										onChange={handlePaymentForm}
										startAdornment={
											<InputAdornment position="start">
												₹
											</InputAdornment>
										}
										sx={{ width: 60 }}
										disabled={viewMode || disable}
										type="number"
									/>
								</FormControl>
							</Grid>
							<Grid item xs={2}></Grid>
						</Grid>
					</Grid>
					{/* <Grid item container xs={12}>
                        <Grid item xs={2}></Grid>
                        <Grid item xs={8} className={classes.paperchildGrid}>
                            <FormControl
                                fullWidth
                                className={classes.formElement}
                                variant="outlined">
                                <InputLabel htmlFor="paymentDueAmount">
                                    Due Amount
                                </InputLabel>
                                <OutlinedInput
                                    id="paymentDueAmount"
                                    name="dueAmount"
                                    value={paymentFormData.dueAmount}
                                    label="Due Amount"
                                    onChange={handlePaymentForm}
                                    disabled={true}
                                    startAdornment={
                                        <InputAdornment
                                            className={classes.currency}
                                            position="start">
                                            ₹
                                        </InputAdornment>
                                    }
                                    labelWidth={60}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={2}></Grid>
                        <Grid item xs={2}></Grid>
                        <Grid item xs={8} className={classes.paperchildGrid}>
                            <DatePicker
                                inputVariant="outlined"
                                value={paymentFormData.dueDate}
                                onChange={(date: Date | null) => handleDueData(date)}
                                format="dd/MM/yyyy"
                                label="Due Date"
                                animateYearScrolling
                                name="dueDate"
                                disablePast={false}
                                className={classes.formElement}
                                disabled={viewMode}
                            />
                        </Grid>
                        <Grid item xs={2}></Grid>
                    </Grid> */}
					<Grid item container xs={12} className={classes.paperchildGrid}>
						<Grid item xs={2}></Grid>
						<Grid item xs={8}>
							<CssTextField
								id="paymentDescription"
								label="Payment Description"
								multiline
								variant="outlined"
								name="description"
								className={classes.formElement}
								value={paymentFormData.description}
								margin="normal"
								rows={4}
								onChange={handlePaymentForm}
								disabled={viewMode}
							/>
						</Grid>
						<Grid item xs={2}></Grid>
					</Grid>
					<Grid
						container
						item
						direction="row"
						justifyContent="space-around"
						className={classes.saveGrid}>
						<Grid item></Grid>
						<Grid item>
							<Button
              style={{backgroundColor:"#e4e5e6",color:"#000000"}}
								className={classes.formElement}
								onClick={handleSavePayment}
								variant="contained">
								SAVE
							</Button>
						</Grid>
						<Grid item></Grid>
					</Grid>
				</form>
				<PaymentTable
					payments={campaign.payments}
					handleDelete={handleDelete}
					handleEdit={handleEdit}
					handleView={handleView}
					isActionsEnabled={true}
				/>
				<Grid
					container
					direction="row"
					justifyContent="space-around"
					className={classes.saveGrid}>
					<Grid item>
						<Button className={classes.formElement} onClick={handleBack}  style={{color:"#000000"}}>
							BACK
						</Button>
					</Grid>
					<Grid item></Grid>
					<Grid item>
						<Button
							className={classes.formElement}
							variant="outlined"
							onClick={handleSubmit}
              style={{color:"#000000",borderColor:"#042630",}}
							endIcon={<Icon>send</Icon>}>
							NEXT
						</Button>
					</Grid>
				</Grid>
			</BaseLayout>
		</Root>
	);
};

export default PaymentDetails;
